import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import { Button } from '../../../web/components/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { charmGray } from '../../../web/styles/colors';
import { AIDA_ASSISTANT_NAME } from '../../hooks/useAISummary';
import { styleSheet } from './styles';

interface PostCallFeedbackPanelProps {
	phoneCallId: string;
	transcriptionId: string;
	onClose: () => void;
}

export const PostCallFeedbackPanel: React.FC<PostCallFeedbackPanelProps> = ({
	phoneCallId,
	transcriptionId,
	onClose,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const [aiResponse, setAiResponse] = React.useState('');
	const [conversationId, setConversationId] = React.useState<string | null>(null);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState(false);
	const [isLoadingTranscription, setIsLoadingTranscription] = React.useState(false);
	const [transcriptionText, setTranscriptionText] = React.useState('');
	const [followUpQuestion, setFollowUpQuestion] = React.useState('');
	const [conversationMessages, setConversationMessages] = React.useState<
		{ role: 'user' | 'assistant'; content: string }[]
	>([]);
	const [lastAiResponseCompleted, setLastAiResponseCompleted] = React.useState(false);

	// Refs to track if mutations have been initiated (prevents duplicate requests)
	const hasInitiatedConversation = React.useRef(false);
	const hasInitiatedAnalysis = React.useRef(false);

	// Fetch transcription speaker lines
	const fetchTranscription = React.useCallback(async () => {
		if (!transcriptionId) return;

		setIsLoadingTranscription(true);
		try {
			const response = await userSession.webServiceHelper.callAsync<any>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { type: 'SpeakerLine' },
					urlPath: `transcription/${transcriptionId}/view`,
				}),
				'GET'
			);

			if (!response?.speakerLines) {
				setTranscriptionText('');
				return;
			}

			const formattedText = response.speakerLines.map((line: any) => `${line.speaker}: ${line.line}`).join('\n');
			setTranscriptionText(formattedText);
		} catch (error) {
			console.error('Failed to fetch transcription:', error);
			errorMessages.pushApiError(error);
		} finally {
			setIsLoadingTranscription(false);
		}
	}, [transcriptionId, userSession, errorMessages]);

	// Create conversation mutation
	const createConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: conversation => {
			setConversationId(conversation.id);
		},
	});

	// Streaming response mutation
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({ convoId, chatRequest }: { convoId: string; chatRequest: Api.AIMessageRequest }) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${AIDA_ASSISTANT_NAME}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, _params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') return;

			const messageText = data?.message?.text;
			if (messageText) {
				if (data.isDelta === false) {
					setAiResponse(data.message.text);
				} else {
					setAiResponse(prev => prev + messageText);
				}
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
		},
	});

	// Fetch transcription on mount
	React.useEffect(() => {
		fetchTranscription();
	}, [fetchTranscription]);

	// Create conversation when transcription is ready
	React.useEffect(() => {
		if (transcriptionText && !conversationId && !hasInitiatedConversation.current) {
			hasInitiatedConversation.current = true;
			createConversationMutation.mutate({
				assistantName: AIDA_ASSISTANT_NAME,
				phoneCallId,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transcriptionText, conversationId, phoneCallId]);

	// Send initial analysis when conversation is ready
	React.useEffect(() => {
		if (conversationId && transcriptionText && !hasFetchedFeedback && !hasInitiatedAnalysis.current) {
			hasInitiatedAnalysis.current = true;
			aiPromptMutation.mutate({
				convoId: conversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: `Please analyze this call transcript and provide coaching feedback:\n\n${transcriptionText}`,
							id: null,
						},
					],
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [conversationId, transcriptionText, hasFetchedFeedback]);

	// Track when AI response is complete
	React.useEffect(() => {
		if (!aiPromptMutation.isLoading && aiResponse && hasFetchedFeedback && !lastAiResponseCompleted) {
			setConversationMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
			setLastAiResponseCompleted(true);
		}
	}, [aiPromptMutation.isLoading, aiResponse, hasFetchedFeedback, lastAiResponseCompleted]);

	const handleSendFollowUp = React.useCallback(() => {
		if (!followUpQuestion.trim() || !conversationId) return;

		setConversationMessages(prev => [...prev, { role: 'user', content: followUpQuestion }]);
		setAiResponse('');
		setHasFetchedFeedback(false);
		hasInitiatedAnalysis.current = false;
		setLastAiResponseCompleted(false);

		aiPromptMutation.mutate({
			convoId: conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: followUpQuestion,
						id: null,
					},
				],
			},
		});

		setFollowUpQuestion('');
	}, [followUpQuestion, conversationId, aiPromptMutation]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendFollowUp();
		}
	};

	const isLoading = isLoadingTranscription || createConversationMutation.isPending || aiPromptMutation.isLoading;

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.headerContent)}>
					<SparklesIcon className={css(styleSheet.headerIcon)} />
					<h3 className={css(styleSheet.title)}>Post-Call AI Feedback</h3>
				</div>
				<button onClick={onClose} className={css(styleSheet.closeButton)}>
					<XIcon color={charmGray} height={12} width={12} />
				</button>
			</div>

			<div className={css(styleSheet.content)}>
				{isLoadingTranscription && (
					<div className={css(styleSheet.loadingContainer)}>
						<LoadingSpinner type='small' />
						<span>Loading transcription...</span>
					</div>
				)}

				{!isLoadingTranscription && !transcriptionText && (
					<div className={css(styleSheet.emptyState)}>
						<p>Unable to load transcription for this call.</p>
					</div>
				)}

				{transcriptionText && !conversationId && (
					<div className={css(styleSheet.loadingContainer)}>
						<LoadingSpinner type='small' />
						<span>Creating AI conversation...</span>
					</div>
				)}

				{conversationId && (
					<div className={css(styleSheet.messagesContainer)}>
						{conversationMessages.map((message, index) => (
							<div
								key={index}
								className={css(
									styleSheet.messageCard,
									message.role === 'user' ? styleSheet.userMessage : styleSheet.assistantMessage
								)}
							>
								<div className={css(styleSheet.messageRole)}>{message.role === 'user' ? 'You' : 'AI Assistant'}</div>
								<div className={css(styleSheet.messageContent)}>
									<AIMarkdownResponse>{message.content}</AIMarkdownResponse>
								</div>
							</div>
						))}

						{/* Show streaming AI response */}
						{aiResponse && (!lastAiResponseCompleted || conversationMessages.length === 0) && (
							<div className={css(styleSheet.messageCard, styleSheet.assistantMessage)}>
								<div className={css(styleSheet.messageRole)}>
									AI Assistant
									{aiPromptMutation.isLoading && (
										<span className={css(styleSheet.streamingIndicator)}>
											<LoadingSpinner type='small' />
											Analyzing...
										</span>
									)}
								</div>
								<div className={css(styleSheet.messageContent)}>
									<AIMarkdownResponse>{aiResponse}</AIMarkdownResponse>
								</div>
							</div>
						)}

						{/* Show loading when waiting for response */}
						{aiPromptMutation.isLoading && !aiResponse && (
							<div className={css(styleSheet.messageCard, styleSheet.assistantMessage)}>
								<div className={css(styleSheet.messageRole)}>AI Assistant</div>
								<div className={css(styleSheet.loadingContainer)}>
									<LoadingSpinner type='small' />
									<span>{conversationMessages.length === 0 ? 'Analyzing your call...' : 'Thinking...'}</span>
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Follow-up input */}
			{conversationId && !isLoading && (
				<div className={css(styleSheet.inputContainer)}>
					<textarea
						className={css(styleSheet.textarea)}
						value={followUpQuestion}
						onChange={e => setFollowUpQuestion(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder='Ask a follow-up question about the call...'
						rows={2}
					/>
					<div className={css(styleSheet.buttonContainer)}>
						<Button kind='secondary' size='small' onClick={handleSendFollowUp} disabled={!followUpQuestion.trim()}>
							Send Question
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
